/**
 * RegistrationForm Component
 *
 * Phase 1 form for basic account creation in the provider registration workflow.
 * Includes fields for email, password, confirmPassword, firstName, and lastName
 * with real-time validation and accessibility features.
 *
 * @module components/provider-registration/RegistrationForm
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { useTheme } from '@/hooks/useTheme';
import { useProviderRegistrationFormState } from '@/hooks/useProviderRegistrationFormState';
import type { RegistrationFormState } from '@/types/registration';

/**
 * RegistrationForm component props interface
 */
export interface RegistrationFormProps {
  /** Form submission handler */
  onSubmit: (data: RegistrationFormState['values']) => Promise<void>;
  /** Loading state */
  isLoading?: boolean;
  /** Initial form data */
  initialData?: Partial<RegistrationFormState['values']>;
  /** Form submission error */
  submitError?: string;
  /** Custom class name */
  className?: string;
  /** Disable form */
  disabled?: boolean;
}

/**
 * RegistrationForm component for Phase 1 of provider registration
 *
 * @example
 * ```tsx
 * <RegistrationForm
 *   onSubmit={handleRegistrationSubmit}
 *   isLoading={isSubmitting}
 *   submitError={submitError}
 * />
 * ```
 */
export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSubmit,
  isLoading = false,
  initialData = {},
  submitError,
  className,
  disabled = false
}) => {
  const { isDark } = useTheme();

  // Use the custom hook for form state and logic
  const {
    formData,
    fieldErrors,
    fieldRefs,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit,
  } = useProviderRegistrationFormState({
    initialData,
    onSubmit,
  });

  return (
    <div className={cn('w-full', className)}>
      {/* Form Header */}
      <div className="text-left mb-8">
        <h1 className={cn(
          "text-2xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Create Your Account
        </h1>
        <p className={cn(
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Join BiteWise to start referring patients to qualified Accredited Practicing Dietitians
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Name Fields Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name Field */}
          <FormField
            label="First Name"
            required
            error={fieldErrors.firstName}
          >
            <Input
              ref={(el) => { fieldRefs.current.firstName = el; }}
              type="text"
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleFieldBlur('firstName')}
              placeholder="Enter your first name"
              autoComplete="given-name"
              disabled={disabled || isLoading}
              required
              fullWidth
            />
          </FormField>

          {/* Last Name Field */}
          <FormField
            label="Last Name"
            required
            error={fieldErrors.lastName}
          >
            <Input
              ref={(el) => { fieldRefs.current.lastName = el; }}
              type="text"
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleFieldBlur('lastName')}
              placeholder="Enter your last name"
              autoComplete="family-name"
              disabled={disabled || isLoading}
              required
              fullWidth
            />
          </FormField>
        </div>

        {/* Email Field */}
        <FormField
          label="Email Address"
          required
          error={fieldErrors.email}
          helperText="We'll use this email for important account notifications"
        >
          <Input
            ref={(el) => { fieldRefs.current.email = el; }}
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={() => handleFieldBlur('email')}
            placeholder="Enter your email address"
            autoComplete="email"
            disabled={disabled || isLoading}
            required
            fullWidth
          />
        </FormField>

        {/* Password Fields Row */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Password Field */}
            <FormField
              label="Password"
              required
              error={fieldErrors.password}
            >
              <Input
                ref={(el) => { fieldRefs.current.password = el; }}
                type="password"
                value={formData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                onBlur={() => handleFieldBlur('password')}
                placeholder="Create a password"
                autoComplete="new-password"
                disabled={disabled || isLoading}
                required
                fullWidth
              />
            </FormField>

            {/* Confirm Password Field */}
            <FormField
              label="Confirm Password"
              required
              error={fieldErrors.confirmPassword}
            >
              <Input
                ref={(el) => { fieldRefs.current.confirmPassword = el; }}
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                onBlur={() => handleFieldBlur('confirmPassword')}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={disabled || isLoading}
                required
                fullWidth
              />
            </FormField>
          </div>
          {/* Password Helper Text */}
          <p className={cn(
            "text-sm",
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            Must be at least 8 characters with uppercase, lowercase, and numbers
          </p>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className={cn(
            "p-4 border rounded-lg",
            isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start">
              <svg className={cn(
                "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
                isDark ? "text-red-400" : "text-red-600"
              )} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className={cn(
                  "text-sm font-medium",
                  isDark ? "text-red-200" : "text-red-800"
                )}>
                  Registration Failed
                </h3>
                <p className={cn(
                  "text-sm mt-1",
                  isDark ? "text-red-300" : "text-red-700"
                )}>
                  {submitError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isLoading}
          disabled={disabled}
          className="w-full"
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>

        {/* Terms and Privacy */}
        <p className={cn(
          "text-xs text-center",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          By creating an account, you agree to our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Terms of Service (opens in new window)"
            className={cn(
              "hover:underline inline-flex items-center gap-1",
              isDark ? "text-primary-400" : "text-primary-600"
            )}
          >
            Terms of Service
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Policy (opens in new window)"
            className={cn(
              "hover:underline inline-flex items-center gap-1",
              isDark ? "text-primary-400" : "text-primary-600"
            )}
          >
            Privacy Policy
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </p>
      </form>
    </div>
  );
};

export default RegistrationForm;
